import { useCallback } from 'react'

import { Err, Result } from 'ts-results'

import { useApplicationStore } from 'app/application/infrastructure'

import { HttpError, useHttp } from '../infrastructure'
import { ApiErrorResponse } from '../types'
import DataError from './dataError'

const handleApiResponse = <R>(
  result: Result<R, HttpError<ApiErrorResponse>>,
): Result<R, DataError<number>> =>
  result.ok
    ? result
    : Err(
        new DataError(
          result.val.code,
          result.val.payload?.messages[0].text ?? result.val.message,
          result.val,
        ),
      )

const executeApiRequest = <R>(
  request: () => Promise<Result<R, HttpError<ApiErrorResponse>>>,
): Promise<Result<R, DataError<number>>> => {
  const { authorization, setAuthorization } = useApplicationStore.getState()

  return request().then((result) => {
    if (result.err && result.val.code === 401 && authorization) {
      setAuthorization({ state: 'expired' })

      // Translate 401 only in case of a former login
      return Err(new DataError(result.val.code, 'Session expired', result.val))
    }

    return handleApiResponse(result)
  })
}

const useApi = () => {
  const { httpDelete, httpGet, httpPatch, httpPost, httpPut } = useHttp()

  const create = useCallback(
    <T, R>(endpoint: string, data: T) =>
      executeApiRequest(() => httpPost<T, R, ApiErrorResponse>(endpoint, data)),
    [httpPost],
  )

  const _delete = useCallback(
    (endpoint: string) => executeApiRequest(() => httpDelete<ApiErrorResponse>(endpoint)),
    [httpDelete],
  )

  const read = useCallback(
    <R>(endpoint: string) => executeApiRequest(() => httpGet<R, ApiErrorResponse>(endpoint)),
    [httpGet],
  )

  const replace = useCallback(
    <T, R>(endpoint: string, data: T) =>
      executeApiRequest(() => httpPut<T, R, ApiErrorResponse>(endpoint, data)),
    [httpPut],
  )

  const update = useCallback(
    <T, R>(endpoint: string, data: T) =>
      executeApiRequest(() => httpPatch<T, R, ApiErrorResponse>(endpoint, data)),
    [httpPatch],
  )

  return {
    create,
    _delete,
    read,
    replace,
    update,
  }
}

export default useApi
